import {
  actionColumn,
  Breadcrumb,
  collapseColumn,
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  IconButton,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  Tag,
  TagGroup,
  Tooltip,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { dateCell } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import {
  BidPackageBidderKind,
  BidScopeSource,
  CostCodeBidSummaryStatus,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { FormattedBidName } from "src/routes/bids/BidPackagesPage";
import { useDeleteBidPackageMutation } from "src/routes/bids/BidPackagesPage.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { createHierarchyBreadcrumbs } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import { createBidPackagesUrl, createBidPackagesV2Url } from "src/RouteUrls";
import { extractPoMessages, queryResult } from "src/utils";
import {
  BidPackagesPageV2_BidPackageFragment,
  BidPackagesPageV2_CostCodeBidSummaryFragment,
  BidPackagesPageV2Query,
  useBidPackagesPageV2Query,
} from "./BidPackagesPageV2.generated";
import { CostCodeBidSummaryBanners } from "./flagBanners";
import {
  BID_PACKAGES_PAGE_LIMIT,
  bidPackageCreateOptions,
  bidPackageHref,
  CostCodeBidSummaryGroup,
  groupCostCodeBidSummaries,
  statusTagType,
} from "./utils";

export function BidPackagesPageV2() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useBidPackagesPageV2Query({
    variables: { projectId, filter: { projects: [projectId] }, page: { limit: BID_PACKAGES_PAGE_LIMIT } },
  });
  return queryResult(query, {
    data: ({ project, costCodeBidSummaries }) => (
      <BidPackagesPageV2View project={project} summaries={costCodeBidSummaries.entities} />
    ),
  });
}

type BidPackagesPageV2ViewProps = {
  project: BidPackagesPageV2Query["project"];
  summaries: BidPackagesPageV2_CostCodeBidSummaryFragment[];
};

function BidPackagesPageV2View(props: BidPackagesPageV2ViewProps) {
  const { project, summaries } = props;
  const { id: projectId, name, bidPackages } = project;
  const onDelete = useDeleteBidPackageConfirmation();
  const columns = useMemo(() => createColumns(onDelete), [onDelete]);
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const rows = useMemo(
    () => createRows(groupCostCodeBidSummaries(summaries, bidPackages), projectId),
    [summaries, bidPackages, projectId],
  );
  // The hierarchy crumbs link to each level's bid packages, e.g. "Los Angeles / Altadena / Bid Packages"
  const breadcrumbs: Breadcrumb[] = [
    ...createHierarchyBreadcrumbs(project, createBidPackagesUrl),
    { label: "Bid Packages", href: createBidPackagesV2Url(projectId) },
  ];
  const actions: HeaderAction[] = [
    {
      kind: "menu",
      trigger: { label: "Create", variant: "primary" },
      items: bidPackageCreateOptions(projectId).map(({ label, url }) => ({ label, onClick: url })),
    },
  ];
  return (
    <PageHeaderLayout
      pageHeader={{ title: "Bid Packages", documentTitleSuffix: name, breadcrumbs: { breadcrumbs }, actions }}
    >
      <GridTableLayout
        hideEditColumns
        emptyFallback="There aren't any cost codes to display."
        tableProps={{ columns, rows, rowStyles }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type SummaryRow = { kind: "summary"; data: CostCodeBidSummaryGroup };
type PackageRow = {
  kind: "package";
  data: { group: CostCodeBidSummaryGroup; bidPackage: BidPackagesPageV2_BidPackageFragment };
};
type Row = HeaderRow | SummaryRow | PackageRow;

function createRowStyles(projectId: string): RowStyles<Row> {
  return {
    summary: {
      cellCss: ({ data: { status } }) =>
        status.code === CostCodeBidSummaryStatus.Missing
          ? Css.bgRed50.$
          : status.code === CostCodeBidSummaryStatus.Incomplete
            ? Css.bgOrange50.$
            : {},
    },
    // The whole row opens the bid package, or reopens the create flow if it's still a first draft, the delete
    // column is an actionColumn, so it stays clickable.
    package: { rowLink: ({ data }) => bidPackageHref(data.bidPackage, projectId) },
  };
}

function createRows(groups: CostCodeBidSummaryGroup[], projectId: string): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...groups.map((group) => ({
      kind: "summary" as const,
      id: group.id,
      data: group,
      // The flags stack under the summary row, also while it's collapsed.
      companion: () =>
        group.flags.nonEmpty ? <CostCodeBidSummaryBanners group={group} projectId={projectId} /> : null,
      // A bid package can sit under several groups (i.e. Labor and Materials), so its row id needs the group prefix
      children: group.bidPackages.map((bidPackage) => ({
        kind: "package" as const,
        id: `${group.id}_${bidPackage.id}`,
        data: { group, bidPackage },
      })),
    })),
  ];
}

function createColumns(onDelete: (bidPackage: BidPackagesPageV2_BidPackageFragment) => void): GridColumn<Row>[] {
  return [
    collapseColumn<Row>(),
    column<Row>({
      header: "Code",
      summary: ({ costCode }) => <div css={Css.xs.$}>{costCode.number}</div>,
      package: ({ group }) => <div css={Css.xs.$}>{group.costCode.number}</div>,
      w: "80px",
    }),
    column<Row>({
      header: "Name",
      w: 2,
      summary: ({ costCode }) => <span css={Css.smSb.$}>{costCode.name}</span>,
      package: ({ bidPackage }) => ({
        content: () => (
          <div css={Css.df.fdr.aic.gap2.xs.$}>
            <FormattedBidName
              costCodesName={bidPackage.costCodesName}
              latestVersion={bidPackage.latestVersion}
              isPlanBasedV2={bidPackage.isPlanBasedV2}
            />
            {bidPackage.isPlanBasedV2 && bidPackage.isMissingOptions && (
              <Tag text="Add Missing Options" icon="error" iconOnly type="warning" />
            )}
          </div>
        ),
        css: Css.sm.$,
      }),
    }),
    column<Row>({
      header: "Cost Type",
      summary: ({ costType }) => costType.name,
      package: ({ bidPackage }) => bidPackage.costType.name,
    }),
    column<Row>({
      header: "Bid Coverage",
      summary: ({ status }) => <Tag text={status.name} type={statusTagType[status.code]} />,
      package: emptyCell,
      w: "125px",
    }),
    column<Row>({
      header: "Bid Type",
      summary: emptyCell,
      package: ({ bidPackage: { isPlanBasedV2, isUnitBased } }) =>
        isPlanBasedV2 ? "Plan V2" : isUnitBased ? "Unit" : "Plan V1",
      w: "80px",
    }),
    column<Row>({
      header: "Product Offerings",
      summary: emptyCell,
      package: ({ bidPackage }) => (
        <ProductOfferings latestVersion={bidPackage.latestVersion} projects={bidPackage.projects} />
      ),
    }),
    column<Row>({
      header: "Hierarchy",
      summary: emptyCell,
      package: ({ bidPackage }) => (
        <BidPackageHierarchy projects={bidPackage.projects} scopeSource={bidPackage.scopeSource} />
      ),
    }),
    column<Row>({
      header: "Status",
      summary: emptyCell,
      package: ({ bidPackage: { status } }) => (
        <div css={Css.df.aic.$}>
          <StatusIndicator status={status.code} type="BidPackage" />
          <span css={Css.ml1.$}>{status.name}</span>
        </div>
      ),
    }),
    column<Row>({
      header: "Source",
      summary: emptyCell,
      package: ({ bidPackage }) => <BidPackageSource bidders={bidPackage.activeBidders} />,
      w: "120px",
    }),
    dateColumn<Row>({
      header: "Last Edited",
      summary: emptyCell,
      package: ({ bidPackage }) => dateCell(bidPackage.updatedAt),
    }),
    actionColumn<Row>({
      header: emptyCell,
      summary: emptyCell,
      package: ({ bidPackage }) => ({
        content: (
          <IconButton
            inc={2}
            icon="trash"
            onClick={() => onDelete(bidPackage)}
            disabled={extractPoMessages(bidPackage.canDelete) || !bidPackage.canDelete.allowed}
          />
        ),
      }),
      w: "80px",
    }),
  ];
}

/** The product offerings on the bid's latest version out of the ones active at its scope. */
function ProductOfferings(props: Pick<BidPackagesPageV2_BidPackageFragment, "latestVersion" | "projects">) {
  const { latestVersion, projects } = props;
  const onBid = latestVersion.readyPlanAggregateVersions.length;
  const total =
    projects
      .flatMap((p) =>
        p.__typename === "Community"
          ? p.productOfferings
          : p.__typename === "Market"
            ? p.children.flatMap((c) => c.productOfferings)
            : [],
      )
      .filter((po) => po.status.code === ReadyPlanStatus.Active).length || onBid;
  if (total === 0) return null;
  return (
    <Tooltip
      title={
        <div css={Css.df.fdc.gap1.$}>
          {latestVersion.readyPlanAggregateVersions.map((rpav) => (
            <div key={rpav.id}>
              {rpav.readyPlan.name} {rpav.displayVersion}
            </div>
          ))}
        </div>
      }
      placement="top"
    >
      <span>{`${onBid === total ? "All " : ""}${onBid}/${total}`}</span>
    </Tooltip>
  );
}

/** The communities the bid covers, or just "Lot" for lot scoped bids, as we don't list out every lot. */
function BidPackageHierarchy({
  projects,
  scopeSource,
}: Pick<BidPackagesPageV2_BidPackageFragment, "projects" | "scopeSource">) {
  const names = scopeSource?.code === BidScopeSource.Lot ? ["Lot"] : projects.map((p) => p.name).unique();
  return <TagGroup tags={names.map((text) => ({ text }))} />;
}

type Bidder = BidPackagesPageV2_BidPackageFragment["activeBidders"][number];

/** Trades show their name, internal estimates their type. */
function BidPackageSource({ bidders }: { bidders: Bidder[] }) {
  if (bidders.isEmpty) return <span css={Css.gray700.$}>No bidders yet</span>;
  const [estimates, trades] = bidders.partition((b) => b.kind === BidPackageBidderKind.InternalEstimate);
  return (
    <div css={Css.df.fdc.gap1.$}>
      {estimates.map((e) => (
        <div key={e.id}>
          {/* Unit based internal estimates have no type, so keep the generic tag for them */}
          <Tag text={e.bidContract?.internalEstimateType?.name ?? "Internal Estimate"} type="update" />
        </div>
      ))}
      {trades.map((t) => (
        <span key={t.id}>{t.name}</span>
      ))}
    </div>
  );
}

/** Confirms the delete, then refetches the grid. */
function useDeleteBidPackageConfirmation() {
  const { openModal } = useModal();
  const [deleteBidPackage] = useDeleteBidPackageMutation({ refetchQueries: ["BidPackagesPageV2"] });
  return useCallback(
    (bidPackage: { id: string; name: string }) =>
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage={
              <>
                Are you sure you want to delete this bid package <b>{bidPackage.name}</b>? It will not be archived and
                cannot be undone.
              </>
            }
            onConfirmAction={async () => {
              await deleteBidPackage({ variables: { id: bidPackage.id } });
            }}
            title="Delete Bid Package"
            label="Yes, Delete"
          />
        ),
      }),
    [deleteBidPackage, openModal],
  );
}
